import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Mail, ChevronRight, ChevronDown, ChevronUp,
  Shield, UserCog, UserPlus, Users, Crown,
  Trash2, ArrowRightLeft, Settings, Link2, FileText,
  MailIcon, ListChecks, Bell, Layers, BookOpen, Layout as LayoutIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONFIG_MENU = [
  { id: 'comptes-acces', label: "Comptes d'accès", icon: Users, href: '/configuration/comptes-acces', active: true },
  { id: 'automatisations', label: 'Automatisations', icon: Settings, href: '#' },
  { id: 'interconnexions', label: 'Interconnexions', icon: Link2, href: '#' },
  { id: 'modeles-documents', label: 'Modèles de documents', icon: FileText, href: '#' },
  { id: 'modeles-emails', label: "Modèles d'emails", icon: MailIcon, href: '#' },
  { id: 'modeles-taches', label: 'Modèles de listes de tâches', icon: ListChecks, href: '#' },
  { id: 'notifications', label: 'Notifications', icon: Bell, href: '#' },
  { id: 'competences', label: 'Domaines de compétences', icon: Layers, href: '#' },
  { id: 'marque-catalogue', label: 'Ma marque - Catalogue en ligne', icon: BookOpen, href: '#' },
  { id: 'formulaire-inscription', label: "Formulaire individuel d'inscription", icon: FileText, href: '#' },
  { id: 'espace-apprenant', label: 'Espace Apprenant', icon: LayoutIcon, href: '#' },
];

const ComptesAccesPage = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({ owner: true, admins: true, intervenants: true });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [newType, setNewType] = useState('Administrateur');
  const [newForm, setNewForm] = useState({ prenom: '', nom: '', email: '', password: '' });
  const [ownerForm, setOwnerForm] = useState({ prenom: '', nom: '', email: '', password: '' });
  const [selectedForInvite, setSelectedForInvite] = useState([]);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [accRes, ownerRes] = await Promise.all([
        fetch(`${API}/config/comptes-acces`, { credentials: 'include' }),
        fetch(`${API}/config/comptes-acces/owner`, { credentials: 'include' }),
      ]);
      if (accRes.ok) {
        const data = await accRes.json();
        setAccounts(data);
        setSelectedForInvite(data.filter(a => !a.is_owner && !a.invited_at).map(a => a.account_id));
      }
      if (ownerRes.ok) setOwner(await ownerRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const adminsAndCommercials = accounts.filter(a => !a.is_owner && (a.type === 'Administrateur' || a.type === 'Commercial'));
  const intervenants = accounts.filter(a => a.type === 'Intervenant');

  const toggleSection = (s) => setExpandedSections(prev => ({ ...prev, [s]: !prev[s] }));

  const handleChangeRole = async (accountId, newRole) => {
    try {
      const res = await fetch(`${API}/config/comptes-acces/${accountId}/role`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ type: newRole })
      });
      if (res.ok) { toast.success(`Rôle changé en ${newRole}`); fetchAll(); }
    } catch (e) { toast.error("Erreur lors du changement de rôle"); }
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm("Supprimer ce compte d'accès ?")) return;
    try {
      await fetch(`${API}/config/comptes-acces/${accountId}`, { method: 'DELETE', credentials: 'include' });
      toast.success("Compte supprimé"); fetchAll();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleCreateAccount = async () => {
    if (!newForm.email) { toast.error("L'email est requis"); return; }
    try {
      const res = await fetch(`${API}/config/comptes-acces`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ ...newForm, type: newType })
      });
      if (res.ok) {
        const data = await res.clone().json().catch(() => null);
        toast.success("Compte créé avec succès"); setShowNewModal(false);
        setNewForm({ prenom: '', nom: '', email: '', password: '' }); fetchAll();
      } else {
        let errorMsg = "Erreur de création";
        try {
          const errData = await res.clone().json();
          if (errData.detail) errorMsg = errData.detail;
        } catch {}
        toast.error(errorMsg);
      }
    } catch (e) {
      console.error("Create account error:", e);
      toast.error("Erreur de connexion. Vérifiez que vous êtes connecté.");
    }
  };

  const handleSendInvitations = async () => {
    try {
      const res = await fetch(`${API}/config/comptes-acces/invite`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ account_ids: selectedForInvite })
      });
      if (res.ok) {
        const data = await res.clone().json().catch(() => ({}));
        toast.success(data.message || "Invitations envoyées"); setShowInviteModal(false); fetchAll();
      }
    } catch (e) { toast.error("Erreur d'envoi"); }
  };

  const toggleInviteSelection = (id) => {
    setSelectedForInvite(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleOpenOwnerModal = () => {
    if (owner && !owner.not_set) {
      setOwnerForm({ prenom: owner.prenom || '', nom: owner.nom || '', email: owner.email || '' });
    } else {
      setOwnerForm({ prenom: '', nom: '', email: '' });
    }
    setShowOwnerModal(true);
  };

  const handleSetOwner = async () => {
    if (!ownerForm.email) { toast.error("L'email est requis"); return; }
    try {
      const res = await fetch(`${API}/config/comptes-acces/owner`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(ownerForm)
      });
      if (res.ok) {
        toast.success("Possesseur du compte defini avec succes");
        setShowOwnerModal(false); fetchAll();
      } else {
        let msg = "Erreur";
        try { const err = await res.clone().json(); msg = err.detail || msg; } catch {}
        toast.error(msg);
      }
    } catch (e) { toast.error("Erreur de connexion"); }
  };

  const handleTransferOwnership = async (accountId) => {
    if (!window.confirm("Transférer la propriété du compte à cet utilisateur ?")) return;
    try {
      const res = await fetch(`${API}/config/comptes-acces/transfer`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ account_id: accountId })
      });
      if (res.ok) { toast.success("Propriete transferee"); fetchAll(); }
      else { let msg = "Erreur"; try { const err = await res.clone().json(); msg = err.detail || msg; } catch {} toast.error(msg); }
    } catch (e) { toast.error("Erreur de connexion"); }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin h-8 w-8 border-4 border-violet-500 border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  const SectionHeader = ({ label, expanded, onToggle, icon: Icon }) => (
    <button onClick={onToggle} className="w-full flex items-center justify-between py-4 group">
      <div className="flex items-center gap-3">
        {Icon && <Icon className="h-5 w-5 text-violet-500" />}
        <h2 className="text-lg font-bold text-slate-800">{label}</h2>
      </div>
      {expanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
    </button>
  );

  const UserRow = ({ account, showRoleToggle }) => (
    <div className="flex items-center justify-between py-3 px-4 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0">
      <div className="flex-1 min-w-0">
        <button onClick={() => navigate(`/configuration/comptes-acces/${account.account_id}`)}
          className="text-sm font-semibold text-violet-600 hover:text-violet-800 hover:underline"
          data-testid={`account-link-${account.account_id}`}>
          {account.nom} {account.prenom}
        </button>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-slate-500">{account.email}</span>
        </div>
        {!account.has_logged_in && (
          <span className="text-[11px] text-slate-400 italic">L'utilisateur ne s'est pas connecté</span>
        )}
      </div>
      <div className="flex items-center gap-4">
        <Badge variant="outline" className="text-xs border-slate-200 text-slate-600" data-testid={`account-type-${account.account_id}`}>
          {account.type}
        </Badge>
        {showRoleToggle && account.type === 'Administrateur' && (
          <Button variant="ghost" size="sm" className="text-xs text-violet-600 hover:text-violet-800"
            onClick={() => handleChangeRole(account.account_id, 'Commercial')}
            data-testid={`toggle-role-${account.account_id}`}>
            Passer en Commercial
          </Button>
        )}
        {showRoleToggle && account.type === 'Commercial' && (
          <Button variant="ghost" size="sm" className="text-xs text-violet-600 hover:text-violet-800"
            onClick={() => handleChangeRole(account.account_id, 'Administrateur')}
            data-testid={`toggle-role-${account.account_id}`}>
            Passer en Administrateur
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-500"
          onClick={() => handleDelete(account.account_id)} data-testid={`delete-${account.account_id}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  const uninvitedAccounts = accounts.filter(a => !a.is_owner);

  return (
    <Layout>
      <div className="flex gap-8" data-testid="comptes-acces-page">
        {/* Left sidebar - Configuration menu */}
        <div className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-24">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 px-3">Configuration</h3>
            <nav className="space-y-0.5">
              {CONFIG_MENU.map((item) => {
                const Icon = item.icon;
                return (
                  <button key={item.id}
                    onClick={() => item.href !== '#' ? navigate(item.href) : null}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                      item.active
                        ? 'bg-violet-50 text-violet-700 font-semibold'
                        : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                    }`}
                    data-testid={`config-menu-${item.id}`}>
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-6">
          {/* Header */}
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <span>Configuration</span>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-slate-600 font-medium">Comptes d'accès</span>
          </div>

          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-black text-slate-900" data-testid="page-title">Configuration des comptes d'accès</h1>
            <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => setShowInviteModal(true)} data-testid="send-invitations-btn">
              <Mail className="h-4 w-4 mr-2" /> Envoyer les invitations
            </Button>
          </div>

          {/* Possesseur du compte */}
          <Card className="border-0 shadow-sm" data-testid="section-owner">
            <CardContent className="p-0">
              <div className="px-6">
                <SectionHeader label="Possesseur du compte" expanded={expandedSections.owner} onToggle={() => toggleSection('owner')} icon={Crown} />
              </div>
              {expandedSections.owner && (
                <div className="px-6 pb-6 border-t border-slate-100 pt-4">
                  {owner && !owner.not_set ? (
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-base font-bold text-slate-900">{owner.nom} {owner.prenom}</h3>
                        <p className="text-sm text-slate-500 mt-0.5">{owner.email}</p>
                        <p className="text-xs text-slate-400 mt-2 leading-relaxed max-w-lg">
                          Le possesseur du compte est le super administrateur qui donne les droits à tous les autres utilisateurs.
                          Son adresse email reçoit par défaut les notifications internes.
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" className="text-violet-600 border-violet-200 hover:bg-violet-50"
                          onClick={handleOpenOwnerModal} data-testid="edit-owner-btn">
                          Modifier
                        </Button>
                        {accounts.filter(a => !a.is_owner).length > 0 && (
                          <Button variant="outline" className="text-violet-600 border-violet-200 hover:bg-violet-50" data-testid="transfer-btn"
                            onClick={handleOpenOwnerModal}>
                            <ArrowRightLeft className="h-4 w-4 mr-2" /> Transférer
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-slate-500 mb-3">Aucun possesseur n'est défini pour ce compte.</p>
                      <p className="text-xs text-slate-400 mb-4">Le possesseur est le super administrateur qui gère les droits d'accès de tous les utilisateurs.</p>
                      <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={handleOpenOwnerModal} data-testid="set-owner-btn">
                        <Crown className="h-4 w-4 mr-2" /> Définir le possesseur du compte
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Administrateurs et Commerciaux */}
          <Card className="border-0 shadow-sm" data-testid="section-admins">
            <CardContent className="p-0">
              <div className="px-6">
                <SectionHeader label="Administrateurs et Commerciaux" expanded={expandedSections.admins} onToggle={() => toggleSection('admins')} icon={Shield} />
              </div>
              {expandedSections.admins && (
                <div className="border-t border-slate-100">
                  <div className="px-6 py-3 flex gap-3">
                    <Button variant="outline" className="text-sm" onClick={() => { setNewType('Administrateur'); setShowNewModal(true); }}
                      data-testid="add-admin-btn">
                      <UserPlus className="h-4 w-4 mr-2" /> Nouvel accès administrateur
                    </Button>
                    <Button variant="outline" className="text-sm" onClick={() => { setNewType('Commercial'); setShowNewModal(true); }}
                      data-testid="add-commercial-btn">
                      <UserPlus className="h-4 w-4 mr-2" /> Nouvel accès commercial
                    </Button>
                  </div>
                  {adminsAndCommercials.length > 0 ? (
                    <div className="border-t border-slate-100">
                      <div className="grid grid-cols-[1fr,auto,auto,auto] items-center px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                        <span>Nom</span>
                        <span className="w-28 text-center">Type</span>
                        <span className="w-40 text-center">Action</span>
                        <span className="w-10"></span>
                      </div>
                      {adminsAndCommercials.map(acc => <UserRow key={acc.account_id} account={acc} showRoleToggle={true} />)}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-6 border-t border-slate-100">Aucun administrateur ou commercial</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Intervenants */}
          <Card className="border-0 shadow-sm" data-testid="section-intervenants">
            <CardContent className="p-0">
              <div className="px-6">
                <SectionHeader label="Intervenants" expanded={expandedSections.intervenants} onToggle={() => toggleSection('intervenants')} icon={UserCog} />
              </div>
              {expandedSections.intervenants && (
                <div className="border-t border-slate-100">
                  <p className="px-6 py-2 text-xs text-slate-400 italic">
                    Les intervenants n'ont pas accès à toutes les capacités d'administration du compte.
                  </p>
                  <div className="px-6 py-3">
                    <Button variant="outline" className="text-sm" onClick={() => { setNewType('Intervenant'); setShowNewModal(true); }}
                      data-testid="add-intervenant-btn">
                      <UserPlus className="h-4 w-4 mr-2" /> Nouvel accès intervenant
                    </Button>
                  </div>
                  {intervenants.length > 0 ? (
                    <div className="border-t border-slate-100">
                      <div className="grid grid-cols-[1fr,auto,auto] items-center px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                        <span>Nom</span>
                        <span className="w-28 text-center">Type</span>
                        <span className="w-10"></span>
                      </div>
                      {intervenants.map(acc => <UserRow key={acc.account_id} account={acc} showRoleToggle={false} />)}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-6 border-t border-slate-100">Aucun intervenant</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* New Account Modal */}
      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent className="sm:max-w-md" data-testid="new-account-modal">
          <DialogHeader>
            <DialogTitle>Nouvel accès {newType.toLowerCase()}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="prenom">Prénom</Label>
                <Input id="prenom" value={newForm.prenom} onChange={e => setNewForm(p => ({ ...p, prenom: e.target.value }))} data-testid="input-prenom" />
              </div>
              <div>
                <Label htmlFor="nom">Nom</Label>
                <Input id="nom" value={newForm.nom} onChange={e => setNewForm(p => ({ ...p, nom: e.target.value }))} data-testid="input-nom" />
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={newForm.email} onChange={e => setNewForm(p => ({ ...p, email: e.target.value }))} data-testid="input-email" />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe (min. 8 caractères)</Label>
              <Input id="password" type="password" value={newForm.password}
                onChange={e => setNewForm(p => ({ ...p, password: e.target.value }))}
                placeholder="Mot de passe sécurisé"
                data-testid="input-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewModal(false)}>Annuler</Button>
            <Button className="bg-violet-600 hover:bg-violet-700" onClick={handleCreateAccount} data-testid="create-account-btn">
              Créer le compte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invitation Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="sm:max-w-md" data-testid="invite-modal">
          <DialogHeader>
            <DialogTitle>
              <span className="text-sm text-slate-400 font-normal block mb-1">Comptes d'accès</span>
              Envoyer des invitations
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 max-h-[400px] overflow-y-auto space-y-2">
            {uninvitedAccounts.map(acc => (
              <label key={acc.account_id} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-slate-50 cursor-pointer">
                <Checkbox
                  checked={selectedForInvite.includes(acc.account_id)}
                  onCheckedChange={() => toggleInviteSelection(acc.account_id)}
                  data-testid={`invite-check-${acc.account_id}`}
                />
                <span className="text-sm text-slate-700">{acc.nom} {acc.prenom}</span>
              </label>
            ))}
            {uninvitedAccounts.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-4">Aucun compte à inviter</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteModal(false)}>Annuler</Button>
            <Button className="bg-violet-600 hover:bg-violet-700" onClick={handleSendInvitations}
              disabled={selectedForInvite.length === 0} data-testid="send-invitations-confirm">
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set/Transfer Owner Modal */}
      <Dialog open={showOwnerModal} onOpenChange={setShowOwnerModal}>
        <DialogContent className="sm:max-w-md" data-testid="owner-modal">
          <DialogHeader>
            <DialogTitle>
              <Crown className="h-5 w-5 text-amber-500 inline mr-2" />
              {owner && !owner.not_set ? 'Modifier le possesseur du compte' : 'Définir le possesseur du compte'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              Le possesseur est le super administrateur qui contrôle les droits d'accès de tous les utilisateurs du compte.
            </p>

            {/* Quick select from existing accounts */}
            {accounts.filter(a => !a.is_owner).length > 0 && (
              <div>
                <Label className="text-sm text-slate-500 mb-1 block">Transférer à un compte existant</Label>
                <div className="max-h-32 overflow-y-auto border rounded-md p-1 space-y-0.5">
                  {accounts.filter(a => !a.is_owner).map(acc => (
                    <button key={acc.account_id}
                      className="w-full text-left px-3 py-2 text-sm rounded hover:bg-violet-50 hover:text-violet-700 flex items-center justify-between"
                      onClick={() => setOwnerForm({ prenom: acc.prenom, nom: acc.nom, email: acc.email })}
                      data-testid={`select-owner-${acc.account_id}`}>
                      <span>{acc.nom} {acc.prenom}</span>
                      <span className="text-xs text-slate-400">{acc.email}</span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 my-3">
                  <div className="h-px bg-slate-200 flex-1" />
                  <span className="text-xs text-slate-400">ou saisir manuellement</span>
                  <div className="h-px bg-slate-200 flex-1" />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="owner-prenom">Prénom</Label>
                <Input id="owner-prenom" value={ownerForm.prenom}
                  onChange={e => setOwnerForm(p => ({ ...p, prenom: e.target.value }))}
                  data-testid="owner-input-prenom" />
              </div>
              <div>
                <Label htmlFor="owner-nom">Nom</Label>
                <Input id="owner-nom" value={ownerForm.nom}
                  onChange={e => setOwnerForm(p => ({ ...p, nom: e.target.value }))}
                  data-testid="owner-input-nom" />
              </div>
            </div>
            <div>
              <Label htmlFor="owner-email">Email *</Label>
              <Input id="owner-email" type="email" value={ownerForm.email}
                onChange={e => setOwnerForm(p => ({ ...p, email: e.target.value }))}
                placeholder="email@exemple.com"
                data-testid="owner-input-email" />
            </div>
            <div>
              <Label htmlFor="owner-password">Mot de passe (min. 8 caractères)</Label>
              <Input id="owner-password" type="password" value={ownerForm.password}
                onChange={e => setOwnerForm(p => ({ ...p, password: e.target.value }))}
                placeholder="Mot de passe sécurisé"
                data-testid="owner-input-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOwnerModal(false)}>Annuler</Button>
            <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={handleSetOwner}
              disabled={!ownerForm.email} data-testid="confirm-owner-btn">
              <Crown className="h-4 w-4 mr-2" /> Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ComptesAccesPage;
